import CONSTANTS from "@/lib/CONSTANTS";
import { getContainerNameForShipment } from "@/lib/utilities";

export const { TASK_CODES, TASK_STATUSES } = CONSTANTS;

export function placeOfReceipt(shipment) {
  return shipment?.summarisedLocations?.locations?.find(
    (location) => location.lookupKey === shipment.summarisedLocations.placeOfReceiptLocationLookupKey
  );
}

export function portOfLoading(shipment) {
  return {
    ...shipment?.summarisedLocations?.locations?.find(
      (location) => location.lookupKey === shipment.summarisedLocations.portOfLoadingLocationLookupKey
    ),
    departure: {
      originalDeparture: shipment.originalEtdLocal ? shipment.originalEtdLocal : "-",
      dateTime: shipment.etdLocalAtPortOfLoading || "-",
      carriage: {
        vehicleName: shipment?.summarisedVessels?.vessels?.find(
          (vessel) => vessel.lookupKey === shipment.summarisedVessels.vesselAtPortOfLoadingLookupKey
        )?.name
      }
    }
  };
}

export function portOfDischarge(shipment) {
  return {
    ...shipment?.summarisedLocations?.locations?.find(
      (location) => location.lookupKey === shipment?.summarisedLocations.portOfDischargeLocationLookupKey
    ),
    arrival: {
      originalArrival: shipment?.originalEtaLocal ? shipment?.originalEtaLocal : "-",
      dateTime: shipment?.etaLocalAtPortOfDischarge || "-",
      carriage: {
        vehicleName: shipment?.summarisedVessels?.vessels?.find(
          (vessel) => vessel.lookupKey === shipment?.summarisedVessels.vesselAtPortOfDischargeLookupKey
        )?.name
      }
    }
  };
}

export function placeOfDelivery(shipment) {
  return shipment?.summarisedLocations?.locations?.find(
    (location) => location.lookupKey === shipment.summarisedLocations.placeOfDeliveryLocationLookupKey
  );
}

export function isMultiLegJourney(shipment) {
  return (
    !!shipment?.summarisedVessels?.vesselAtPortOfLoadingLookupKey &&
    !!shipment?.summarisedVessels?.vesselAtPortOfDischargeLookupKey &&
    shipment?.summarisedVessels?.vessels?.length > 1
  );
}

export function commodityDescription(shipment) {
  const containerDetails = getContainerDetails(shipment);
  if (containerDetails.allContainersCommodities.size === 1) {
    return [...containerDetails.allContainersCommodities][0];
  } else if (containerDetails.allContainersCommodities.size === 0) {
    return "-";
  } else {
    return CONSTANTS.CARGO_VARIOUS_COMMODITIES;
  }
}

export function hyphenIfEmpty(value) {
  return value || "-";
}

export function filteredTasks(shipment) {
  return shipment?.tasks?.filter(
    (task) => carrierTasksImport(task) || carrierTasksExport(task) || userTasksImport(task) || userTasksExport(task)
  );
}

export function carrierTasksImport(task) {
  return task.taskCode === TASK_CODES.ARRANGE_CARGO_RELEASE && task.statusCode === TASK_STATUSES.IN_PROGRESS;
}

export function carrierTasksExport(task) {
  return (
    (task.taskCode === TASK_CODES.CONFIRM_SHIPMENT && task.statusCode === TASK_STATUSES.IN_PROGRESS) ||
    (task.taskCode === TASK_CODES.ISSUE_ORIGINAL_TRANSPORT_DOCUMENT && task.statusCode === TASK_STATUSES.OPEN) ||
    (task.taskCode === TASK_CODES.ISSUE_VERIFY_COPY_OF_TRANSPORT_DOCUMENT &&
      task.statusCode === TASK_STATUSES.PENDING) ||
    (task.taskCode === TASK_CODES.DANGEROUS_GOODS_DECLARATION_RECEIVED && task.statusCode === TASK_STATUSES.IN_PROGRESS)
  );
}

export function userTasksImport(task) {
  return (
    (task.taskCode === TASK_CODES.ARRANGE_CARGO_RELEASE && task.statusCode === TASK_STATUSES.OPEN) ||
    task.taskCode === TASK_CODES.MARK_ORIGINAL_TRANSPORT_DOCUMENT_AS_RECEIVED
  );
}

export function userTasksExport(task) {
  return (
    (task.taskCode === TASK_CODES.RECEIVE_TRANSPORT_DOCUMENT_INSTRUCTIONS && task.statusCode === TASK_STATUSES.OPEN) ||
    (task.taskCode === TASK_CODES.CONFIRM_SHIPMENT && task.statusCode === TASK_STATUSES.CLOSED) ||
    (task.taskCode === TASK_CODES.RESOLVE_MISSING_VGM_DATA &&
      (task.statusCode === TASK_STATUSES.OPEN ||
        task.statusCode === TASK_STATUSES.PENDING ||
        task.statusCode === TASK_STATUSES.IN_PROGRESS)) ||
    (task.taskCode === TASK_CODES.ISSUE_VERIFY_COPY_OF_TRANSPORT_DOCUMENT &&
      task.statusCode === TASK_STATUSES.CLOSED) ||
    (task.taskCode === TASK_CODES.ISSUE_AMENDED_VERIFY_COPY_OF_TRANSPORT_DOCUMENT &&
      task.statusCode === TASK_STATUSES.CLOSED) ||
    (task.taskCode === TASK_CODES.ISSUE_ORIGINAL_TRANSPORT_DOCUMENT && task.statusCode === TASK_STATUSES.CLOSED) ||
    (task.taskCode === TASK_CODES.DANGEROUS_GOODS_DECLARATION_RECEIVED && task.statusCode === TASK_STATUSES.OPEN)
  );
}

/**
 * @name parseResponseAsShipments
 * @description Parses a summarisedBillsOfLading endpoint response into a collection of shipments with their corresponding information.
 * @param response A complete summarisedBillsOfLading API response
 * @return {Array} A collection of shipments
 * @return {String} Tab section name
 */
export function parseResponseAsShipments(response, tabSection = "") {
  const allShipmentsData = [];
  if (response?.length > 0) {
    let index = 0;
    for (const shipment of response) {
      /**
       *  Collect all the containers, container types, container sizes and commodities to set the 'Cargo' details.
       */

      const containerDetails = getContainerDetails(shipment);

      if (
        new Date(shipment.originalEtaLocal).getTime() !== new Date(shipment.etaLocalAtPortOfDischarge).getTime() ||
        tabSection !== "eta"
      ) {
        allShipmentsData.push({
          id: index,
          exportServiceMode: shipment.exportServiceMode,
          importServiceMode: shipment.importServiceMode,
          parties: shipment.parties,
          ladingNumber: hyphenIfEmpty(shipment.id),
          bookedByRef: shipment?.bookedByReferences?.[0],
          placeOfReceipt: placeOfReceipt(shipment),
          portOfLoading: portOfLoading(shipment),
          placeOfDelivery: placeOfDelivery(shipment),
          portOfDischarge: portOfDischarge(shipment),
          etaLastUpdated: hyphenIfEmpty(shipment.etaLastUpdatedUtc),
          etaChangeReason: shipment.etaChangeReason,
          multiLegJourney: isMultiLegJourney(shipment),
          cargo: {
            containerName: getContainerNameForShipment(
              containerDetails.allContainersTypes,
              containerDetails.allContainersSizes,
              containerDetails.allContainersDisplayNames
            ),
            containerQuantity: hyphenIfEmpty(containerDetails?.allContainers?.size),
            commodity: commodityDescription(shipment)
          },
          tasks: filteredTasks(shipment),
          shipments: shipment.shipments
        });
      }

      index += 1;
    }
  }

  return allShipmentsData;
}

export function getContainerDetails(shipment) {
  const allContainers = new Set();
  const allContainersTypes = new Set();
  const allContainersSizes = new Set();
  const allContainersDisplayNames = new Set();
  const allContainersCommodities = new Set();

  shipment.shipments &&
    shipment.shipments.forEach((s) => {
      s.containers &&
        s.containers.forEach((c) => {
          allContainers.add(c);
        });
      s.containers &&
        s.containers.forEach((c) => {
          allContainersTypes.add(c.type);
          allContainersSizes.add(c.size);
          allContainersDisplayNames.add(c.sizeTypeDisplayName);
          c.commodities && c.commodities.forEach((cd) => allContainersCommodities.add(cd.commodityName));
        });
    });
  return { allContainers, allContainersTypes, allContainersSizes, allContainersDisplayNames, allContainersCommodities };
}
